import React from 'react';
import { Box, Typography, Dialog } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { IBankDetailsForm } from '../../bankDetails';
import { BasicDetailsFormDefaultValues, IBasicDetailsForm } from '../../basicDetails';
import { IAllData } from '../../KYCsteps';
import { PAN_STATUS_VARIANTS } from 'constants/panStatusVariants';
import { useSelector } from 'react-redux';
import { ErrorAlertIcon } from '../../../../shared/ErrorAlertIcon';
import { ErrorAlert } from '../../../../shared/ErrorAlert';
import { basicDetailsOCRStyles } from './BasicDetailsOCR.styles';
import { useTranslation } from 'react-i18next';

interface BasicDetailsOCRProps {
  nextStep?: (
    data: IBasicDetailsForm | IBankDetailsForm,
    basicDetailsOCRProps?: {
      uploadedPanImage?: string | null;
      panStatus?: string;
      validationMessage?: string;
      formData: IAllData;
      panResponse?: {
        fullName?: string;
        [key: string]: unknown;
      } | null;
    }
  ) => void;
  onDelete?: () => void;
  uploadedPanImage?: string | null;
  panStatus?: string;
  validationMessage?: string;
  formData: IAllData;
  panResponse?: {
    fullName?: string;
    [key: string]: unknown;
  } | null;
  onPanValidationUpdate?: (payload: {
    panStatus: (typeof PAN_STATUS_VARIANTS)[keyof typeof PAN_STATUS_VARIANTS];
    panResponse: Record<string, unknown>;
    validationMessage?: string;
  }) => void;
  manualEntryState?: {
    panNumber: string;
    verifiedName: string;
    verifiedStatus: string;
    establishmentType: string;
    isManualMode: boolean;
  };
  onManualEntryStateChange?: (state: {
    panNumber: string;
    verifiedName: string;
    verifiedStatus: string;
    establishmentType: string;
    isManualMode: boolean;
  }) => void;
  hideHeader?: boolean;
  embedded?: boolean;
  inlineFooter?: boolean;
  /** Overrides the default "Next" label (e.g. when this screen is used as a read-only PAN view). */
  primaryCtaLabel?: string;
}

export function BasicDetailsOCR({
  nextStep,
  onDelete,
  uploadedPanImage,
  panStatus,
  panResponse,
  validationMessage,
  formData,
  hideHeader = false,
  embedded = false,
  inlineFooter = false,
  primaryCtaLabel,
}: BasicDetailsOCRProps) {
  const { t } = useTranslation();
  const info = useSelector((state: any) => state?.info?.allData);
  const [tds10ModalOpen, setTds10ModalOpen] = React.useState(false);
  const [tds20ModalOpen, setTds20ModalOpen] = React.useState(false);

  const isSuccess = panStatus === 'PAN_VERIFIED';
  const normalizedValidationMessage = React.useMemo(() => {
    const message = String(validationMessage || '').trim();
    if (!message) {
      return '';
    }

    if (
      /pan.*(not|could not|unable to).*(identify|identified|read)/i.test(message) ||
      /poor image quality/i.test(message)
    ) {
      return t('pan_poor_image_quality');
    }

    return message;
  }, [validationMessage]);

  const statusMessage = normalizedValidationMessage || (isSuccess
    ? t('pan_valid')
    : panStatus === 'PAN_INOPERATIVE'
      ? t('pan_inoperative')
      : panStatus === 'PAN_INACTIVE' ? t('pan_inactive') : t('pan_verification_pending'));

  const statusColor = isSuccess && !normalizedValidationMessage ? '#007d00' : '#b71b1c';
  const panStatusForTds = panStatus as string | undefined;
  const showEmbeddedTds10Notice =
    embedded && panStatusForTds === PAN_STATUS_VARIANTS.PAN_VERIFIED;
  const showEmbeddedTds20Notice =
    embedded && (
      panStatusForTds === PAN_STATUS_VARIANTS.PAN_INOPERATIVE ||
      panStatusForTds === PAN_STATUS_VARIANTS.PAN_INACTIVE
    );
  const tds20ModalReason: 'inactive' | 'inoperative' =
    panStatusForTds === PAN_STATUS_VARIANTS.PAN_INOPERATIVE ? 'inoperative' : 'inactive';

  const isNextEnabled = isSuccess ||
    panStatus === PAN_STATUS_VARIANTS.PAN_INACTIVE ||
    panStatus === PAN_STATUS_VARIANTS.PAN_INOPERATIVE;

  const handleNext = () => {
    if (!isNextEnabled) {
      return;
    }

    const nextData: IBasicDetailsForm = {
      ...BasicDetailsFormDefaultValues,
      establishmentType: formData?.establishmentType || '',
      mobileNumber: formData?.mobileNumber || '',
      panNumber: String((panResponse?.panNumber as string | undefined) || formData?.panNumber || ''),
      tdsStatus: !isSuccess,
    };

    nextStep?.(nextData, {
      uploadedPanImage,
      panStatus,
      validationMessage,
      formData,
      panResponse,
    });
  };

  return (
    <Box sx={embedded ? basicDetailsOCRStyles.embeddedRoot : basicDetailsOCRStyles.root}>
      <Box sx={embedded ? basicDetailsOCRStyles.embeddedContent : basicDetailsOCRStyles.content}>
        {!hideHeader && (
          <Box sx={basicDetailsOCRStyles.header}>
            <Typography sx={basicDetailsOCRStyles.stepText}>
              {t("upload_pan_step_text")}
            </Typography>
            <Box sx={basicDetailsOCRStyles.titleRow}>
              <Typography sx={basicDetailsOCRStyles.titleText}>
                {t("upload_pan_card_title")}
              </Typography>
              <Typography sx={basicDetailsOCRStyles.requiredText}>
                *
              </Typography>
            </Box>
            <Typography sx={basicDetailsOCRStyles.subtitleText}>
              {t("upload_pan_card_subtitle")}
            </Typography>
          </Box>
        )}

        {!embedded && (
          <>
            {uploadedPanImage && (
              <Box
                component="img"
                src={uploadedPanImage}
                alt="Uploaded PAN"
                sx={{ width: '100%', maxHeight: 240, objectFit: 'contain', borderRadius: '8px' }}
              />
            )}
          </>
        )}

        {/* OCR result */}
        <Box sx={basicDetailsOCRStyles.ocrStatusRow}>
          {isSuccess ? (
            <CheckCircleIcon sx={basicDetailsOCRStyles.ocrSuccessIcon} />
          ) : (panStatus === PAN_STATUS_VARIANTS.PAN_INACTIVE || panStatus === PAN_STATUS_VARIANTS.PAN_INOPERATIVE) ? (
            <Box sx={{ mt: '2px', display: 'flex' }}>
              <ErrorAlert />
            </Box>
          ) : (
            <Box sx={basicDetailsOCRStyles.errorIconWrap}>
              <ErrorAlertIcon />
            </Box>
          )}
          <Box>
            {isSuccess && (
              <>
                <Typography sx={basicDetailsOCRStyles.ocrStatusText(statusColor)}>
                  {statusMessage}
                </Typography>
                {panResponse?.fullName && (
                  <Typography sx={basicDetailsOCRStyles.ocrNameText(statusColor)}>
                    {panResponse?.fullName}
                  </Typography>
                )}
              </>
            )}
            {!isSuccess && (panStatus === PAN_STATUS_VARIANTS.PAN_INACTIVE || panStatus === PAN_STATUS_VARIANTS.PAN_INOPERATIVE) && (
              <>
                <Typography sx={basicDetailsOCRStyles.ocrStatusText(statusColor)}>
                  {statusMessage}
                </Typography>
                {panResponse?.fullName && (
                  <Typography sx={basicDetailsOCRStyles.ocrNameText(statusColor)}>
                    {panResponse?.fullName}
                  </Typography>
                )}
              </>
            )}
            {!isSuccess && panStatus !== PAN_STATUS_VARIANTS.PAN_INACTIVE && panStatus !== PAN_STATUS_VARIANTS.PAN_INOPERATIVE && (
              <Typography sx={basicDetailsOCRStyles.retryText}>
                {t('pan_poor_image_quality')}
              </Typography>
            )}
          </Box>
        </Box>

        {showEmbeddedTds10Notice && (
          <Box sx={{ ...basicDetailsOCRStyles.tds10Notice, mt: 2 }}>
            <Typography sx={basicDetailsOCRStyles.tds10NoticeText}>
              {t('pan_available_message')}{' '}
              <Typography
                component="span"
                onClick={() => setTds10ModalOpen(true)}
                sx={basicDetailsOCRStyles.tds10Link}
              >
                {t('learn_more')}
              </Typography>
            </Typography>
          </Box>
        )}
        {showEmbeddedTds20Notice && (
          <Box sx={{ ...basicDetailsOCRStyles.tds20Notice, mt: 2 }}>
            <Typography sx={basicDetailsOCRStyles.tds20NoticeText}>
              {t('pan_inoperative_message')}{' '}
              <Typography
                component="span"
                onClick={() => setTds20ModalOpen(true)}
                sx={basicDetailsOCRStyles.tds20Link}
              >
                {t('learn_more')}
              </Typography>
            </Typography>
          </Box>
        )}
      </Box>

      {/* Sticky bottom button */}
      <Box sx={inlineFooter ? basicDetailsOCRStyles.inlineFooter : basicDetailsOCRStyles.stickyFooter}>
        <Box
          onClick={isNextEnabled ? handleNext : undefined}
          sx={basicDetailsOCRStyles.nextButton(isNextEnabled)}
        >
          <Typography sx={basicDetailsOCRStyles.buttonText}>
            {primaryCtaLabel || t('next')}
          </Typography>
        </Box>
      </Box>

      <Dialog
        open={tds10ModalOpen}
        onClose={() => setTds10ModalOpen(false)}
        PaperProps={{ sx: basicDetailsOCRStyles.tdsModalPaper }}
      >
        <Box sx={basicDetailsOCRStyles.tdsModalContainer}>
          <Box sx={basicDetailsOCRStyles.tdsModalHeader}>
            <Typography sx={basicDetailsOCRStyles.tdsModalTitle}>
              {t('ten_percent_tax_message')}
            </Typography>
            <Box onClick={() => setTds10ModalOpen(false)} sx={basicDetailsOCRStyles.modalCloseButton}>
              <svg fill="none" viewBox="0 0 14 14" width="14" height="14">
                <path d="M14 1.41L12.59 0L7 5.59L1.41 0L0 1.41L5.59 7L0 12.59L1.41 14L7 8.41L12.59 14L14 12.59L8.41 7L14 1.41Z" fill="black" fillOpacity="0.56" />
              </svg>
            </Box>
          </Box>
          <Typography sx={basicDetailsOCRStyles.modalBodyText}>
            {t('ten_percent_tax_details_message1')}
          </Typography>
          <Typography sx={basicDetailsOCRStyles.modalBodyText}>
            {t('ten_percent_tax_details_message2')}
          </Typography>
        </Box>
      </Dialog>

      <Dialog
        open={tds20ModalOpen}
        onClose={() => setTds20ModalOpen(false)}
        PaperProps={{ sx: basicDetailsOCRStyles.tdsModalPaper }}
      >
        <Box sx={basicDetailsOCRStyles.tdsModalContainer}>
          <Box sx={basicDetailsOCRStyles.tdsModalHeader}>
            <Typography sx={basicDetailsOCRStyles.tdsModalTitle}>
              {t('twenty_percent_tax_question')}
            </Typography>
            <Box onClick={() => setTds20ModalOpen(false)} sx={basicDetailsOCRStyles.modalCloseButton}>
              <svg fill="none" viewBox="0 0 14 14" width="14" height="14">
                <path d="M14 1.41L12.59 0L7 5.59L1.41 0L0 1.41L5.59 7L0 12.59L1.41 14L7 8.41L12.59 14L14 12.59L8.41 7L14 1.41Z" fill="black" fillOpacity="0.56" />
              </svg>
            </Box>
          </Box>
          <Typography sx={basicDetailsOCRStyles.modalBodyText}>
            {tds20ModalReason === 'inoperative'
              ? t('twenty_percent_tax_details_message1')
              : t('twenty_percent_tax_details_message2')}
          </Typography>
        </Box>
      </Dialog>

    </Box>
  );
}
